import type { AnalyticsProperties, AnalyticsSink, AnalyticsTrackOptions, AnalyticsUser } from './types';

/**
 * Module-level registry of analytics sinks, keyed by name for idempotent
 * (re-)registration and incremental composition.
 */
const sinks = new Map<string, AnalyticsSink>();

/**
 * Register (or replace, by name) analytics sinks. This is the double/triple-write
 * mechanism: adding a PostHog sink here makes every {@link trackEvent} and
 * {@link identifyAnalyticsUser} call reach it automatically.
 */
export function registerAnalyticsSinks(next: AnalyticsSink[]): void {
  for (const sink of next) {
    sinks.set(sink.name, sink);
  }
}

/** Remove all registered sinks. Primarily for tests. */
export function clearAnalyticsSinks(): void {
  sinks.clear();
}

/** Fan out with per-sink isolation so one vendor failing never breaks the rest. */
function forEachSink(run: (sink: AnalyticsSink) => void): void {
  for (const sink of sinks.values()) {
    try {
      run(sink);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`[analytics] sink "${sink.name}" threw`, error);
    }
  }
}

/** Track a product event across all registered analytics vendors. */
export function trackEvent(event: string, properties?: AnalyticsProperties, options?: AnalyticsTrackOptions): void {
  forEachSink((sink) => sink.track(event, properties, options));
}

/** Identify the current user across all registered analytics vendors. */
export function identifyAnalyticsUser(user: AnalyticsUser): void {
  forEachSink((sink) => sink.identify(user));
}
